import { PropsWithChildren, useEffect, useState } from "react";
import Header from "./Header";
import { EmbedConfigContext, sdkOptionsKey } from "../../EmbedConfigContext";
import useConfigStore from "../../useConfigStore";
import { Alert, Layout } from "antd";
import { loadSdkSchema } from "../../sdkSchema";
import { Content } from "antd/es/layout/layout";
import GleanWebSDK from "@gleanwork/web-sdk"

const PageLayout = ({ children }: PropsWithChildren) => {
  const [ready, setReady] = useState(false)
  const contextValue = useConfigStore()
  const sdkSource = contextValue.config[sdkOptionsKey].source
  const [schemaStatus, setSchemaStatus] = useState<"loading" | "ready" | "fallback">("loading")

  useEffect(() => {
    const controller = new AbortController()
    setSchemaStatus("loading")
    loadSdkSchema(sdkSource, controller.signal).then((usedFallback) => {
      setSchemaStatus(usedFallback ? "fallback" : "ready")
    }).catch((error) => {
      if (!controller.signal.aborted) console.error("Unable to initialize SDK schema", error)
    })
    return () => controller.abort()
  }, [sdkSource])

  useEffect(() => {
    try {
      const sdkSource = contextValue.config?.[sdkOptionsKey]?.source
      if (!sdkSource) {
        throw new Error('No source URL provided')
      }
      if (sdkSource.toLowerCase() === "npm") {
        window.GleanWebSDK = GleanWebSDK
        setReady(true)
      } else {
        const sdkIntegrity = contextValue.config?.[sdkOptionsKey]?.integrity

        const script = document.createElement('script')
        script.src = sdkSource
        if (sdkIntegrity) {
          // https://developer.mozilla.org/en-US/docs/Web/Security/Subresource_Integrity
          script.integrity = sdkIntegrity
          script.setAttribute('crossorigin', 'anonymous')
        }
        script.onload = () => {
          setReady(true)
        }
        document.head.appendChild(script)
      }
    } catch (e) {
      alert('Please provide a source URL in the "SDK Options" section of the config')
    }
  }, [])

  return <EmbedConfigContext.Provider value={contextValue}>
    <Layout className="h-full w-full">
      {schemaStatus === "fallback" && sdkSource.toLowerCase() !== "npm" && (
        <Alert
          type="warning"
          showIcon
          message="The schema for this SDK is unavailable. Using the bundled fallback; options may differ from this SDK."
        />
      )}
      {schemaStatus !== "loading" && <Header />}
      <Content role="main" className="w-full flex-auto bg-white">
        {schemaStatus === "loading" ? "Loading SDK schema…" : ready ? children : "loading..."}
      </Content>
    </Layout>
  </EmbedConfigContext.Provider>
};

export default PageLayout;
